/**
 * This component is used to select multiple prescheduled appointments in the future with filters
 */

import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeDateRangePicker } from "@/Components/NextBase/LimeDateRangePicker";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeServiceSelect } from "@/Components/NextBase/LimeServiceSelect";
import { api } from "@/lib/api-client";
import { DayOfWeek, PostSearchPrescheduledAppointments } from "@/server-types";
import { cn, localizeDayOfWeek } from "@/utils";
import {
  Button,
  Checkbox,
  Chip,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
  RangeValue,
} from "@heroui/react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Plural, Trans } from "@lingui/react/macro";
import { useForm, UseFormReturnType } from "@mantine/form";
import { Calendar, ChevronDown, Clock, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  getValidationErrorMessage,
  ValidationError,
} from "./CustomerSubscriptions/CustomerSubscriptionItem";
import { RiServiceFill } from "react-icons/ri";
import { useClientFeature } from "@/hooks/useClientFeature";
import {
  parseDate,
  CalendarDate,
  getLocalTimeZone,
  today,
} from "@internationalized/date";
import dayjs from "dayjs";

/**
 * TODO: Maybe optimize this component when having to display
 * a lot of appointments. We could use a virtualized list to
 * only render the appointments that are visible in the viewport.
 * Maybe TanStack Virtual (https://tanstack.com/virtual/latest)?
 */
const MAX_GLOBAL_SUBSCRIPTION_ERRORS_DISPLAYED = 5;
const ALL_DAYS_OF_WEEK: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_OF_WEEK_BY_DAYJS_INDEX: Record<number, DayOfWeek> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

type Form = Omit<
  PostSearchPrescheduledAppointments["body"],
  | "date"
  | "locationIds"
  | "customerSubscriptionId"
  | "alreadySelectedAppointmentIds"
  | "attendeeCustomerId"
> & {
  dateRange: RangeValue<CalendarDate> | null;
  locationIds: number[] | undefined;
};

type PrescheduledAppointment =
  PostSearchPrescheduledAppointments["response"]["appointments"][number];

// The server drops these bookings, so the row is locked rather than pretending
// the selection would take effect.
function isDuplicateBlocked(appointment: PrescheduledAppointment) {
  return (
    appointment.isCustomerAlreadyBooked &&
    !appointment.allowDuplicateCustomerOnAppointment
  );
}

export const PrescheduledSelectModal = ({
  onClose,
  isOpen,
  overrideLocationIds,
  overrideCustomerId,
  attendeeCustomerId,
  overrideServiceIds,
  overrideDateRange,
  excludeAppointmentIds,
  customerSubscriptionId,
  appointmentDate,
  fromAppointmentId,
  waitlistMode,
}: {
  onClose: (
    selectedIds?: number[],
    selectedAppointments?: PostSearchPrescheduledAppointments["response"]["appointments"],
  ) => void;
  isOpen: boolean;
  overrideLocationIds?: number[];
  overrideCustomerId?: number;
  // Marks the appointments this customer is already on; unlike
  // `overrideCustomerId` it does not narrow the list.
  attendeeCustomerId?: number | null;
  overrideServiceIds?: number[];
  overrideDateRange?: { start: CalendarDate; end?: CalendarDate };
  excludeAppointmentIds?: number[];
  customerSubscriptionId?: number | null;
  appointmentDate?: string;
  fromAppointmentId?: number;
  waitlistMode?: boolean;
}) => {
  // Calculate default date range
  const defaultDateRange = useMemo(() => {
    const startDate =
      overrideDateRange?.start ??
      (appointmentDate
        ? (() => {
            try {
              return parseDate(dayjs(appointmentDate).format("YYYY-MM-DD"));
            } catch {
              console.log("error parsing appointmentDate", appointmentDate);
              return today(getLocalTimeZone());
            }
          })()
        : today(getLocalTimeZone()));

    const endDate = overrideDateRange?.end ?? startDate.add({ years: 1 });

    return { start: startDate, end: endDate };
  }, [appointmentDate, overrideDateRange]);

  function getDefaultDayInWeek(): DayOfWeek[] {
    if (!appointmentDate) {
      return ALL_DAYS_OF_WEEK;
    }

    const dayIndex = dayjs(appointmentDate).day();
    const dayOfWeek = DAY_OF_WEEK_BY_DAYJS_INDEX[dayIndex];

    if (!dayOfWeek) {
      return ALL_DAYS_OF_WEEK;
    }

    return [dayOfWeek];
  }

  const form = useForm<Form>({
    initialValues: {
      userIds: [],
      dayInWeek: getDefaultDayInWeek(),
      resourceIds: [],
      serviceIds: overrideServiceIds ?? [],
      locationIds: overrideLocationIds ?? [],
      dateRange: defaultDateRange,
      customerId: overrideCustomerId ?? undefined,
    },
  });

  useEffect(() => {
    if (isOpen) {
      form.setFieldValue("dayInWeek", getDefaultDayInWeek());
    }
  }, [isOpen]);

  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState<
    number[]
  >([]);

  // Fetch customer data if customerId is provided
  const { data: customerData } = api.customer.useGetCustomer({
    customerId: overrideCustomerId,
    enabled: !!overrideCustomerId && isOpen,
  });

  const values = form.getValues();
  const body: PostSearchPrescheduledAppointments["body"] = useMemo(() => {
    return {
      userIds: values.userIds ?? [],
      dayInWeek: values.dayInWeek ?? [],
      resourceIds: values.resourceIds ?? [],
      serviceIds: values.serviceIds ?? [],
      locationIds: values.locationIds ?? [],
      date:
        values.dateRange?.start && values.dateRange?.end
          ? {
              from: values.dateRange.start.toString(),
              to: values.dateRange.end.toString(),
            }
          : undefined,
      customerId: values.customerId,
      attendeeCustomerId: attendeeCustomerId ?? undefined,
      customerSubscriptionId: customerSubscriptionId,
      alreadySelectedAppointmentIds: selectedAppointmentIds,
    };
  }, [
    values,
    selectedAppointmentIds,
    customerSubscriptionId,
    attendeeCustomerId,
  ]);

  const {
    data: prescheduledAppointmentsData,
    isFetching: isFetchingPrescheduledAppointments,
    processedErrorMessage: getPrescheduledAppointmentsErrorMessage,
  } = api.appointments.useGetPrescheduledAppointments(body, isOpen);

  const prescheduledAppointments = useMemo(() => {
    if (!prescheduledAppointmentsData) return [];
    if (waitlistMode) {
      return prescheduledAppointmentsData.appointments.filter(
        (a) => a.numberOfCustomers >= a.maxNumberOfCustomers,
      );
    }
    return prescheduledAppointmentsData.appointments;
  }, [prescheduledAppointmentsData, waitlistMode]);

  const alreadySelectedAppointmentValidationResult =
    prescheduledAppointmentsData?.alreadySelectedAppointmentValidationResult;

  const { data: services } = api.service.useGetServices({});

  function getAppointmentErrorDisplayString(error: ValidationError) {
    if (!error.appointment) return "";
    const serviceForAppointment = services?.services.find(
      (service) => service.serviceId === error.appointment.serviceId,
    );
    if (!serviceForAppointment) return "";

    const date = error.appointment.startTimeUtc
      ? new Date(error.appointment.startTimeUtc).toLocaleString(i18n.locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";
    return `${serviceForAppointment.name} ${date}`;
  }

  const areAnyAppointmentsFull = prescheduledAppointments.some(
    (a) =>
      selectedAppointmentIds.includes(a.appointmentId) &&
      a.maxNumberOfCustomers <= a.numberOfCustomers,
  );

  // A selection can turn stale between refetches, so blocked ids are dropped
  // here and not only at the checkbox.
  const confirmable = useMemo(() => {
    const appointments = prescheduledAppointmentsData?.appointments ?? [];
    const blockedIds = new Set(
      appointments.filter(isDuplicateBlocked).map((a) => a.appointmentId),
    );
    // Selections survive a filter change, so ids outside the current result
    // set stay confirmable.
    const appointmentIds = selectedAppointmentIds.filter(
      (id) => !blockedIds.has(id),
    );
    const confirmableIds = new Set(appointmentIds);

    return {
      appointmentIds,
      appointments: appointments.filter((a) =>
        confirmableIds.has(a.appointmentId),
      ),
    };
  }, [prescheduledAppointmentsData, selectedAppointmentIds]);

  const modalTitle = useMemo(() => {
    if (customerData) {
      const { name, lastName } = customerData;
      return t`Izberi termine za ${name} ${lastName}`;
    }
    return t`Izberi termine`;
  }, [customerData]);

  return (
    <LimeModal isOpen={isOpen} onClose={() => onClose()} size="4xl">
      <ModalContent>
        <ModalHeader className="px-4">{modalTitle}</ModalHeader>
        <ModalBody className="px-4 py-0">
          {alreadySelectedAppointmentValidationResult &&
            !alreadySelectedAppointmentValidationResult.isValid &&
            alreadySelectedAppointmentValidationResult.errors.length > 0 && (
              <LimeAlert
                color="danger"
                className="mb-4"
                message={
                  <div>
                    <Trans>
                      Nekateri izbrani termini niso veljavni za uporabo paketa:
                    </Trans>
                    <ul className="mt-2 list-disc pl-5">
                      {alreadySelectedAppointmentValidationResult.errors
                        .slice(0, MAX_GLOBAL_SUBSCRIPTION_ERRORS_DISPLAYED)
                        .map((error, idx) => (
                          <li key={idx}>
                            <span className="font-semibold">
                              {getAppointmentErrorDisplayString(error)}:
                            </span>{" "}
                            {getValidationErrorMessage(error)}
                          </li>
                        ))}

                      {alreadySelectedAppointmentValidationResult.errors
                        .length > MAX_GLOBAL_SUBSCRIPTION_ERRORS_DISPLAYED && (
                        <li>
                          <Trans>
                            In še{" "}
                            {alreadySelectedAppointmentValidationResult.errors
                              .length -
                              MAX_GLOBAL_SUBSCRIPTION_ERRORS_DISPLAYED}{" "}
                            drugih napak...
                          </Trans>
                        </li>
                      )}
                    </ul>
                  </div>
                }
              />
            )}

          <Filters
            form={form}
            overrideServiceIds={overrideServiceIds}
            defaultDayInWeek={getDefaultDayInWeek()}
            defaultDateRange={defaultDateRange}
          />

          <LimeAlert message={getPrescheduledAppointmentsErrorMessage} />

          <div className="mt-4 flex flex-col items-start justify-between gap-2 md:flex-row md:items-center md:gap-0">
            <div className="flex flex-row items-center gap-2">
              <Chip variant="flat">
                {confirmable.appointmentIds.length}{" "}
                <Plural
                  value={confirmable.appointmentIds.length}
                  one={"izbran"}
                  two={"izbrana"}
                  other={"izbranih"}
                />
              </Chip>

              {areAnyAppointmentsFull && !waitlistMode && (
                <Chip variant="flat" color="danger">
                  <Trans>Nekateri termini so polni</Trans>
                </Chip>
              )}

              <Button
                size="sm"
                variant="flat"
                color="default"
                onPress={() => setSelectedAppointmentIds([])}
              >
                <Trans>Počisti izbrane</Trans>
              </Button>
            </div>
            {/* <div className="flex flex-row items-center gap-2">
              <Button
                size="sm"
                variant="bordered"
                startContent={<UserPlus size={16} />}
                isDisabled
              >
                <Trans>Dodaj stranko</Trans>
              </Button>
              <Button
                size="sm"
                color="danger"
                startContent={<Trash2 size={16} />}
                variant="flat"
                isDisabled
              >
                <Trans>Izbriši izbrane</Trans>
              </Button>
            </div> */}
          </div>

          <Divider />

          {isFetchingPrescheduledAppointments &&
          prescheduledAppointments.length === 0 ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-lg" />
              ))}
            </div>
          ) : (
            <AppointmentList
              prescheduledAppointments={prescheduledAppointments}
              selectedAppointmentIds={selectedAppointmentIds}
              setSelectedAppointmentIds={setSelectedAppointmentIds}
              fromAppointmentId={fromAppointmentId}
              excludeAppointmentIds={excludeAppointmentIds}
              waitlistMode={waitlistMode}
            />
          )}
        </ModalBody>
        <ModalFooter className="px-4">
          <Button color="default" variant="flat" onPress={() => onClose()}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            color="primary"
            onPress={() =>
              onClose(confirmable.appointmentIds, confirmable.appointments)
            }
          >
            <Trans>Potrdi</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
};

const AppointmentList = ({
  prescheduledAppointments,
  selectedAppointmentIds,
  setSelectedAppointmentIds,
  fromAppointmentId,
  excludeAppointmentIds,
  waitlistMode,
}: {
  prescheduledAppointments: PostSearchPrescheduledAppointments["response"]["appointments"];
  selectedAppointmentIds: number[];
  setSelectedAppointmentIds: (ids: number[]) => void;
  fromAppointmentId?: number;
  excludeAppointmentIds?: number[];
  waitlistMode?: boolean;
}) => {
  const groupedAppointments = useMemo(() => {
    if (!prescheduledAppointments) return [];

    // Define day order for sorting
    const dayOrder = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7,
    };

    // Group appointments by day of week and start time
    const byDayAndTime = prescheduledAppointments.reduce(
      (acc, appointment) => {
        const dayOfWeek = appointment.dayOfWeek;
        const startTime = new Date(appointment.startTimeUtc).toLocaleTimeString(
          i18n.locale,
          {
            hour: "2-digit",
            minute: "2-digit",
          },
        );

        const key = `${dayOfWeek}-${startTime}`;

        if (!acc[key]) {
          acc[key] = {
            dayOfWeek,
            startTime,
            startTimeUtc: appointment.startTimeUtc,
            appointments: [],
          };
        }

        acc[key].appointments.push(appointment);

        return acc;
      },
      {} as Record<
        string,
        {
          dayOfWeek: string;
          startTime: string;
          startTimeUtc: string;
          appointments: PostSearchPrescheduledAppointments["response"]["appointments"][number][];
        }
      >,
    );

    // Convert to array and sort by day of week and start time
    return Object.values(byDayAndTime).sort((a, b) => {
      // First sort by day of week
      const dayA = dayOrder[a.dayOfWeek as keyof typeof dayOrder] || 8;
      const dayB = dayOrder[b.dayOfWeek as keyof typeof dayOrder] || 8;

      if (dayA !== dayB) {
        return dayA - dayB;
      }

      // Then sort by start time
      return (
        new Date(a.startTimeUtc).getTime() - new Date(b.startTimeUtc).getTime()
      );
    });
  }, [prescheduledAppointments]);

  return (
    <div className="flex flex-col gap-2">
      {groupedAppointments.map((group) => (
        <AppointmentGroup
          key={`${group.dayOfWeek}-${group.startTime}`}
          dayOfWeek={group.dayOfWeek}
          startTime={group.startTime}
          appointments={group.appointments}
          selectedAppointmentIds={selectedAppointmentIds}
          setSelectedAppointmentIds={setSelectedAppointmentIds}
          fromAppointmentId={fromAppointmentId}
          excludeAppointmentIds={excludeAppointmentIds}
          waitlistMode={waitlistMode}
        />
      ))}

      {groupedAppointments.length === 0 && (
        <div className="flex flex-col items-center justify-center">
          <p className="text-sm text-gray-500">
            <Trans>Ni skupinskih terminov</Trans>
          </p>
        </div>
      )}
    </div>
  );
};

const AppointmentGroup = ({
  dayOfWeek,
  startTime,
  appointments,
  selectedAppointmentIds,
  setSelectedAppointmentIds,
  fromAppointmentId,
  excludeAppointmentIds,
  waitlistMode,
}: {
  dayOfWeek: string;
  startTime: string;
  appointments: PostSearchPrescheduledAppointments["response"]["appointments"];
  selectedAppointmentIds: number[];
  setSelectedAppointmentIds: (ids: number[]) => void;
  fromAppointmentId?: number;
  excludeAppointmentIds?: number[];
  waitlistMode?: boolean;
}) => {
  const containsFromAppointment =
    fromAppointmentId != null
      ? appointments.some((a) => a.appointmentId === fromAppointmentId)
      : false;

  const [isOpen, setIsOpen] = useState(containsFromAppointment);

  // Capitalize day of week for display
  const localizedDayOfWeek = localizeDayOfWeek(dayOfWeek);
  const displayDay =
    (localizedDayOfWeek ?? "").charAt(0).toUpperCase() +
    (localizedDayOfWeek ?? "").slice(1);

  const {
    selectableAppointments,
    alreadyBookedCount,
    areAllAppointmentsSelected,
  } = useMemo(() => {
    const excludedIds = new Set(excludeAppointmentIds);
    const selectedIds = new Set(selectedAppointmentIds);
    const selectable = appointments.filter(
      (appointment) =>
        !isDuplicateBlocked(appointment) &&
        !excludedIds.has(appointment.appointmentId),
    );

    return {
      selectableAppointments: selectable,
      alreadyBookedCount: appointments.filter(
        (appointment) => appointment.isCustomerAlreadyBooked,
      ).length,
      areAllAppointmentsSelected:
        selectable.length > 0 &&
        selectable.every((appointment) =>
          selectedIds.has(appointment.appointmentId),
        ),
    };
  }, [appointments, excludeAppointmentIds, selectedAppointmentIds]);

  return (
    <div className="rounded-xl border-1 border-gray-200 p-2">
      <button
        className={cn(
          "flex w-full flex-row items-center justify-between",
          "transition-all duration-150",
          {
            "pb-2": isOpen,
          },
        )}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="flex flex-row items-center gap-2">
          <Checkbox
            isSelected={areAllAppointmentsSelected}
            isDisabled={selectableAppointments.length === 0}
            onValueChange={() => {
              // only affect the selected appointments inside this group
              const groupIds = new Set(
                selectableAppointments.map(
                  (appointment) => appointment.appointmentId,
                ),
              );

              if (areAllAppointmentsSelected) {
                // Keep all selected appointments except those in current group
                setSelectedAppointmentIds(
                  selectedAppointmentIds.filter((id) => !groupIds.has(id)),
                );
              } else {
                setSelectedAppointmentIds([
                  ...new Set([...selectedAppointmentIds, ...groupIds]),
                ]);
              }
            }}
          />

          <p className="flex flex-row items-center gap-2 text-sm font-medium">
            <Calendar size={16} color="gray" /> {displayDay}
          </p>
          <p className="flex flex-row items-center gap-2 text-sm font-medium">
            <Clock size={16} color="gray" /> {startTime}
          </p>
          <Chip size="sm" variant="bordered">
            {appointments.length}{" "}
            <Plural
              value={appointments.length}
              one={"termin"}
              two={"termina"}
              other={"termini"}
            />
          </Chip>
          {alreadyBookedCount > 0 && (
            <Chip size="sm" variant="flat" color="warning">
              {alreadyBookedCount}{" "}
              <Plural
                value={alreadyBookedCount}
                one={"že prijavljen"}
                two={"že prijavljena"}
                other={"že prijavljenih"}
              />
            </Chip>
          )}
        </div>
        <div>
          <ChevronDown
            size={16}
            color="gray"
            className={cn("transition-transform duration-300", {
              "rotate-180": isOpen,
            })}
          />
        </div>
      </button>

      <LimeCollapse isOpen={isOpen}>
        <Divider className="mb-2" />

        <div className="flex flex-col gap-6 p-2 pb-0">
          {appointments.map((appointment, index) => (
            <Appointment
              key={appointment.appointmentId}
              appointment={appointment}
              isLast={index === appointments.length - 1}
              selectedAppointmentIds={selectedAppointmentIds}
              setSelectedAppointmentIds={setSelectedAppointmentIds}
              isExcluded={excludeAppointmentIds?.includes(
                appointment.appointmentId,
              )}
              isAppointmentDate={
                fromAppointmentId != null
                  ? appointment.appointmentId === fromAppointmentId
                  : false
              }
              waitlistMode={waitlistMode}
            />
          ))}
        </div>
      </LimeCollapse>
    </div>
  );
};

const Appointment = ({
  appointment,
  isLast,
  selectedAppointmentIds,
  setSelectedAppointmentIds,
  isAppointmentDate,
  isExcluded,
  waitlistMode,
}: {
  appointment: PrescheduledAppointment;
  isLast?: boolean;
  selectedAppointmentIds: number[];
  setSelectedAppointmentIds: (ids: number[]) => void;
  isAppointmentDate?: boolean;
  isExcluded?: boolean;
  waitlistMode?: boolean;
}) => {
  const isBlocked = isDuplicateBlocked(appointment);

  return (
    <Checkbox
      isDisabled={isExcluded || isBlocked}
      aria-label="Prescheduled appointment"
      classNames={{
        base: cn(
          "p-2 bg-gray-50",
          "inline-flex w-full max-w-md",
          "hover:bg-content2 items-center justify-start",
          "cursor-pointer rounded-lg gap-2 border-2 border-transparent",
          {
            "mb-0": isLast,
            "border-primary shadow-[0_0_0_3px_hsl(var(--heroui-primary)/0.25)]":
              isAppointmentDate,
          },
        ),
      }}
      className="max-w-none"
      isSelected={
        isExcluded ||
        isBlocked ||
        selectedAppointmentIds.includes(appointment.appointmentId)
      }
      onValueChange={() => {
        if (selectedAppointmentIds.includes(appointment.appointmentId)) {
          setSelectedAppointmentIds(
            selectedAppointmentIds.filter(
              (id) => id !== appointment.appointmentId,
            ),
          );
        } else {
          setSelectedAppointmentIds([
            ...selectedAppointmentIds,
            appointment.appointmentId,
          ]);
        }
      }}
    >
      <div className="flex w-full flex-col gap-2 md:flex-row md:items-center">
        <div className="flex items-center gap-2">
          <div className="flex flex-row items-center gap-2">
            <User size={16} className="shrink-0" />
            <p
              className={cn("text-sm font-medium", {
                "text-danger font-bold":
                  !waitlistMode &&
                  appointment.numberOfCustomers >=
                    appointment.maxNumberOfCustomers,
              })}
            >
              {appointment.numberOfCustomers} /{" "}
              {appointment.maxNumberOfCustomers}
            </p>
          </div>
          <div className="flex flex-row items-center gap-2">
            <Clock size={16} className="shrink-0" />
            <p className="text-sm font-medium">
              {appointment.startTimeFormatted}
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2">
          <RiServiceFill size={16} className="shrink-0" />
          <p className="text-sm font-medium">{appointment.serviceName}</p>
        </div>
        <div className="flex flex-row items-center gap-2">
          <User size={16} className="shrink-0" />
          <p className="text-sm font-medium">
            {appointment.userNames.join(", ")}
          </p>
        </div>
        {appointment.isCustomerAlreadyBooked && (
          <Chip size="sm" variant="flat" color="warning">
            <Trans>Že na terminu</Trans>
          </Chip>
        )}
      </div>
    </Checkbox>
  );
};

const Filters = ({
  form,
  overrideServiceIds,
  defaultDayInWeek,
  defaultDateRange,
}: {
  form: UseFormReturnType<Form>;
  overrideServiceIds?: number[];
  defaultDayInWeek: DayOfWeek[];
  defaultDateRange: RangeValue<CalendarDate>;
}) => {
  const { data: users, isFetching: isFetchingUsers } = api.user.useUserList({
    respectCalendarPermissions: true,
  });

  const { data: resources, isFetching: isFetchingResources } =
    api.resources.useGetResources({});

  const { data: resourceGroups, isFetching: isFetchingResourceGroups } =
    api.resourceGroups.useGetResourceGroups({});

  const { data: services, isFetching: isFetchingServices } =
    api.service.useGetServices({});

  const { data: locations, isFetching: isFetchingLocations } =
    api.location.useGetLocations({
      sortBy: "labelAsc",
    });

  const { isFeatureEnabled } = useClientFeature();

  useEffect(() => {
    if (users) {
      form.setFieldValue(
        "userIds",
        users.users.map((user) => user.userId),
      );
    }
  }, [users]);

  useEffect(() => {
    if (resources) {
      form.setFieldValue(
        "resourceIds",
        resources.map((resource) => resource.id),
      );
    }
  }, [resources]);

  useEffect(() => {
    if (services && !overrideServiceIds) {
      form.setFieldValue(
        "serviceIds",
        services.services.map((service) => service.serviceId),
      );
    }
  }, [services]);

  const daysOfWeek: { key: DayOfWeek; label: string; shortLabel: string }[] = [
    { key: "monday", label: t`Ponedeljek`, shortLabel: t`Pon` },
    { key: "tuesday", label: t`Torek`, shortLabel: t`Tor` },
    { key: "wednesday", label: t`Sreda`, shortLabel: t`Sre` },
    { key: "thursday", label: t`Četrtek`, shortLabel: t`Čet` },
    { key: "friday", label: t`Petek`, shortLabel: t`Pet` },
    { key: "saturday", label: t`Sobota`, shortLabel: t`Sob` },
    { key: "sunday", label: t`Nedelja`, shortLabel: t`Ned` },
  ];

  const handleDayToggle = (day: DayOfWeek) => {
    const currentDays = form.getValues().dayInWeek;
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d: DayOfWeek) => d !== day)
      : [...currentDays, day];
    form.setFieldValue("dayInWeek", newDays);
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-gray-100 p-4">
      {/* Locations */}
      <LimeSelect
        variant="faded"
        selectionMode="multiple"
        disallowEmptySelection={false}
        isLoading={isFetchingLocations}
        label={t`Lokacije`}
        items={
          locations?.locations?.map((location) => ({
            label: location.label,
            key: location.locationId.toString(),
          })) ?? []
        }
        value={form.getValues().locationIds}
        onMultipleItemChange={(values) => {
          form.setFieldValue("locationIds", values.map(Number));
        }}
        showSelectAllToggle={true}
      />

      {/* Users */}
      <LimeSelect
        variant="faded"
        selectionMode="multiple"
        disallowEmptySelection={false}
        isLoading={isFetchingUsers}
        label={t`Zaposleni`}
        items={
          users?.users.map((user) => ({
            label: user.name ?? "",
            key: user.userId.toString(),
            sectionName: user.role ?? undefined,
          })) ?? []
        }
        value={form.getValues().userIds}
        onMultipleItemChange={(values) => {
          form.setFieldValue("userIds", values.map(Number));
        }}
        showSelectAllToggle={true}
      />

      {/* Resources */}
      {isFeatureEnabled("resources") && (
        <LimeSelect
          variant="faded"
          selectionMode="multiple"
          disallowEmptySelection={false}
          isLoading={isFetchingResources || isFetchingResourceGroups}
          label={t`Sredstvo`}
          items={
            resources?.map((resource) => {
              const group = resourceGroups?.resourceGroups.find(
                (group) => group.id === resource.resourceGroupId,
              );

              return {
                label: resource.label ?? "",
                key: resource.id.toString(),
                sectionName: group?.name ?? undefined,
              };
            }) ?? []
          }
          value={form.getValues().resourceIds}
          onMultipleItemChange={(values) => {
            form.setFieldValue("resourceIds", values.map(Number));
          }}
          showSelectAllToggle={true}
        />
      )}

      {/* Services */}
      <LimeServiceSelect
        variant="faded"
        isLoading={isFetchingServices}
        value={form.getValues().serviceIds}
        onChange={(serviceIds) => {
          form.setFieldValue("serviceIds", serviceIds);
        }}
      />

      {/* Days of Week - Horizontal Checkboxes */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-gray-600">
          <Trans>Dnevi</Trans>
        </span>
        <div className="flex flex-row flex-wrap gap-2">
          {daysOfWeek.map((day) => (
            <Checkbox
              key={day.key}
              isSelected={form.getValues().dayInWeek.includes(day.key)}
              onValueChange={() => handleDayToggle(day.key)}
            >
              {day.shortLabel}
            </Checkbox>
          ))}
        </div>
      </div>

      {/* Date Range */}
      <LimeDateRangePicker
        label={t`Razpon datuma`}
        variant="faded"
        showClearButton
        {...form.getInputProps("dateRange")}
      />

      <div className="flex flex-row gap-2">
        <Button
          size="sm"
          variant="flat"
          color="default"
          onPress={() => {
            form.setValues({
              userIds: users?.users.map((user) => user.userId) ?? [],
              resourceIds: resources?.map((resource) => resource.id) ?? [],
              serviceIds:
                services?.services.map((service) => service.serviceId) ?? [],
              dayInWeek: defaultDayInWeek,
              dateRange: defaultDateRange,
            });
          }}
        >
          <Trans>Ponastavi filtre</Trans>
        </Button>
      </div>
    </div>
  );
};
